import type { Href } from "expo-router";
import { useLocalSearchParams } from "expo-router";
import { useFleetActivation } from "@visioni/a3s-monitoring/app";
import { patriotToolsHref } from "../routes";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { ActivationDetailView } from "../components/ActivationDetailView";
import { ActivationStatus } from "../components/ActivationStatus";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { EMPTY, formatInstant, hasText } from "../format";

const rawId = (id: string) => id;

/**
 * One activation opened from the fleet list, by ticket alone.
 *
 * Links into a tenant appear only when the directory holds it; otherwise they
 * would lead to a tenant route that cannot resolve.
 */
export function FleetActivationScreen() {
  const { ticketNo = "" } = useLocalSearchParams<{ ticketNo?: string }>();
  const { activation, isLoading, error, refetch } = useFleetActivation(ticketNo);
  const tenantName = useDirectoryTenantName();

  const owner = activation?.a3sTenant;
  const ownerName = owner === undefined ? undefined : tenantName(owner);
  const known = owner !== undefined && ownerName !== undefined ? owner : undefined;

  return (
    <PatriotToolsPage
      title={`Activation ${ticketNo}`}
      {...(activation !== undefined && {
        subtitle: [activation.description, `created ${formatInstant(activation.createdDateTime)}`]
          .filter(hasText)
          .join(" · "),
        titleAccessory: <ActivationStatus activation={activation} />,
      })}
    >
      {activation === undefined ?
        <QueryNotice isLoading={isLoading} error={error} onRetry={refetch} />
      : <ActivationDetailView
          activation={activation}
          tenant={
            owner === undefined ? { label: EMPTY }
            : known === undefined ?
              { label: owner }
            : { label: ownerName ?? owner, href: patriotToolsHref(known, "tenant-activations") }
          }
          siteLabel={rawId}
          cameraLabel={rawId}
          {...(known !== undefined && {
            cameraHref: (siteId: string, cameraKey: string) =>
              `/tenants/${known}/sites/${siteId}/cameras/${cameraKey}` as Href,
            listHref: (filters: { site: string; camera?: string }) =>
              patriotToolsHref(known, "tenant-activations", filters),
          })}
        />
      }
    </PatriotToolsPage>
  );
}
